import { Fragment, useState } from 'react';
import type { JSX } from 'react';
import { Button, StatusBadge, cn } from '@taki/ui';
import type { EtatDuCarnet, Groupe, LigneDuCarnet } from './carnet.js';
import { datePureEnTexte } from './temps.js';

/** L'etat d'une ligne, en pastille ; « jamais fait » n'alarme que si le protocole est obligatoire. */
export function etatEnPastille(etat: EtatDuCarnet, obligatoire: boolean): { label: string; tone: 'danger' | 'accent' | 'success' | 'neutral' } {
  if (etat === 'retard') return { label: 'En retard', tone: 'danger' };
  if (etat === 'bientot') return { label: 'Bientôt', tone: 'accent' };
  if (etat === 'ok') return { label: 'À jour', tone: 'success' };
  return obligatoire ? { label: 'Jamais fait', tone: 'danger' } : { label: 'Pas fait', tone: 'neutral' };
}

const GROUPES: readonly Groupe[] = ['Vaccins', 'Antiparasitaires'];

/**
 * LE CARNET DE VACCINATION de l'animal (logique dans `carnet.ts`) : une ligne
 * par protocole, en deux sections, Vaccins puis Antiparasitaires. Obligatoire
 * ou conseille, l'etat, la derniere injection (produit, lot), la prochaine
 * date. Un clic sur la ligne deplie toutes ses injections. « Ajouter
 * aujourd'hui » pose un produit qui couvre le protocole dans les soins de la
 * consultation ; s'il y en a plusieurs, on choisit.
 */
export function Carnet({
  espece,
  lignes,
  produitsPour,
  peutAjouter,
  onAjouter,
}: {
  espece: string;
  lignes: readonly LigneDuCarnet[];
  produitsPour: (protocole: string) => Array<{ id: string; nom: string }>;
  /** Faux quand la consultation est cloturee : ses soins sont figes. */
  peutAjouter: boolean;
  onAjouter: (productId: string) => Promise<void>;
}): JSX.Element {
  const [depliee, setDepliee] = useState<string | null>(null);
  const [choix, setChoix] = useState<string | null>(null);
  if (lignes.length === 0) {
    return (
      <p className="text-sm text-ink-500">
        {espece === '' ? "L'espèce de l'animal n'est pas renseignée : le carnet ne sait pas quoi attendre." : `Aucun protocole prévu pour l'espèce « ${espece} ».`}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-6">
      {GROUPES.map((groupe) => {
        const siennes = lignes.filter((l) => l.groupe === groupe);
        if (siennes.length === 0) return null;
        return (
          <section key={groupe} className="overflow-hidden rounded-md border border-border bg-surface shadow-sm">
            <h3 className="border-b border-border-soft bg-surface-1 px-5 py-3 text-base font-semibold text-ink-900">{groupe}</h3>
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="border-b border-border-soft text-left text-xs text-ink-500">
                  <th className="w-1/4 px-5 py-2 font-semibold">Protocole</th>
                  <th className="w-32 py-2 font-semibold">État</th>
                  <th className="py-2 font-semibold">Dernière fois</th>
                  <th className="w-32 py-2 font-semibold">Prochaine</th>
                  <th className="w-52 py-2 pr-5" />
                </tr>
              </thead>
              <tbody>
                {siennes.map((l) => {
                  const derniere = l.injections[0];
                  const pastille = etatEnPastille(l.etat, l.obligatoire);
                  const ouverte = depliee === l.protocole;
                  const possibles = produitsPour(l.protocole);
                  const aAjouter = peutAjouter && l.etat !== 'ok' && possibles.length > 0;
                  return (
                    <Fragment key={l.protocole}>
                      <tr className={cn('border-b border-border-soft', l.injections.length > 0 && 'cursor-pointer hover:bg-surface-1')} onClick={() => l.injections.length > 0 && setDepliee(ouverte ? null : l.protocole)}>
                        <td className="px-5 py-2">
                          <span className="font-semibold text-ink-900">{l.protocole}</span>
                          <span className="ml-2 text-xs text-ink-500">{l.obligatoire ? 'obligatoire' : 'conseillé'}</span>
                        </td>
                        <td className="py-2">
                          <StatusBadge label={pastille.label} tone={pastille.tone} />
                        </td>
                        <td className="py-2 text-ink-900">
                          {derniere === undefined ? (
                            <span className="text-ink-400">Jamais</span>
                          ) : (
                            <span className="flex flex-col">
                              <span>
                                {datePureEnTexte(derniere.date)} · {derniere.produit}
                              </span>
                              <span className="text-xs text-ink-500">
                                {derniere.lot === '' ? 'lot non noté' : `lot ${derniere.lot}`}
                                {l.injections.length > 1 ? ` · ${l.injections.length} injections` : ''}
                              </span>
                            </span>
                          )}
                        </td>
                        <td className={cn('py-2 tabular-nums', l.etat === 'retard' ? 'text-danger' : 'text-ink-700')}>
                          {l.prochaine === null ? (l.etat === 'jamais' ? 'À proposer' : '') : datePureEnTexte(l.prochaine)}
                        </td>
                        <td className="py-2 pr-5 text-right" onClick={(e) => e.stopPropagation()}>
                          {!aAjouter ? null : possibles.length === 1 ? (
                            <Button size="sm" variant="secondary" onClick={() => void onAjouter(possibles[0]?.id ?? '')}>
                              + Ajouter aujourd'hui
                            </Button>
                          ) : (
                            <Button size="sm" variant="secondary" onClick={() => setChoix(choix === l.protocole ? null : l.protocole)}>
                              + Ajouter aujourd'hui…
                            </Button>
                          )}
                        </td>
                      </tr>
                      {choix === l.protocole && aAjouter && possibles.length > 1 ? (
                        <tr className="border-b border-border-soft bg-surface-1">
                          <td colSpan={5} className="px-5 py-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-semibold text-ink-500">Avec</span>
                              {possibles.map((p) => (
                                <Button
                                  key={p.id}
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => {
                                    setChoix(null);
                                    void onAjouter(p.id);
                                  }}
                                >
                                  {p.nom}
                                </Button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                      {ouverte ? (
                        <tr className="border-b border-border-soft bg-surface-1">
                          <td colSpan={5} className="px-5 py-2">
                            <ul className="flex flex-col gap-1 text-xs text-ink-700">
                              {l.injections.map((i) => (
                                <li key={i.soinId} className="flex gap-3">
                                  <span className="w-24 tabular-nums">{datePureEnTexte(i.date)}</span>
                                  <span className="flex-1">{i.produit}</span>
                                  <span>{i.lot === '' ? 'lot non noté' : `lot ${i.lot}`}</span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}
